import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { AcExpoDiscountCodeForm } from "../schemas";
import { DiscountCode } from "@prisma/client";
import acExpoDiscountCodeQueryKey from "../query-keys/discount-codes.query-keys";
import { toDatabaseFormat } from "@ac-expo/utils";
import { isAxiosError } from "axios";
import { handleApiCall } from "../api-error-handler";
import { AddTicket } from "./basket.mutations";
import { DiscountCodeCheck } from "@ac-expo/api-types";

const getDataFromFormData = (data: AcExpoDiscountCodeForm) => ({
  ...data,
  validityDates: data.validityDates.map(toDatabaseFormat),
});

const createDiscountCode = (data: AcExpoDiscountCodeForm) =>
  handleApiCall(() =>
    privateAxios.post<DiscountCode>(
      `/discount-codes`,
      getDataFromFormData(data)
    )
  );

export const useCreateDiscountAcExpo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoDiscountCodeQueryKey.all,
      });
    },
  });
};

const updateAcExpoDiscountCode = ({ id, ...rest }: AcExpoDiscountCodeForm) =>
  handleApiCall(() =>
    privateAxios.patch<DiscountCode>(
      `/discount-codes/${id}`,
      getDataFromFormData(rest)
    )
  );

export const useUpdateAcExpoDiscountCodeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAcExpoDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoDiscountCodeQueryKey.all,
      });
    },
  });
};

const deleteAcExpoDiscountCode = async (id: string) => {
  const { data: response } = await privateAxios.delete(`/discount-codes/${id}`);
  return response;
};

export const useDeleteAcExpoDiscountCodeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAcExpoDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoDiscountCodeQueryKey.all,
      });
    },
  });
};

const disableAcExpoDiscountCode = async (id: string) => {
  const { data: response } = await privateAxios.patch(
    `/discount-codes/${id}/disable`
  );
  return response;
};

export const useDisableAcExpoDiscountCodeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disableAcExpoDiscountCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoDiscountCodeQueryKey.all,
      });
    },
  });
};

const checkDiscountCode = async (data: {
  promotionCode: string;
  currentUsedCodeNames: string[];
  tickets: AddTicket[];
  badges: AddTicket[];
}) => {
  try {
    const { data: response } = await privateAxios.post<{
      code: DiscountCodeCheck;
      total: number;
      totalDiscount: number;
    }>(`/discount-codes/check-validity`, data);

    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useCheckDiscountCodeMutation = () => {
  return useMutation({
    mutationFn: checkDiscountCode,
  });
};
